import React, { useState, useEffect } from 'react';
import { X, Share, Smartphone, Plus, MoreVertical } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export default function InstallPrompt() {
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [promptEvent, setPromptEvent] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isAppStandalone = window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && (navigator as any).standalone);
    setIsStandalone(!!isAppStandalone);

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIosDevice);

    // Intercept standard PWA install prompt
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setPromptEvent(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setIsModalOpen(true);
      return;
    }

    if (promptEvent) {
      try {
        await promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;
        
        if (outcome === 'accepted') {
          setPromptEvent(null);
        } else {
          // Se o usuário recusar o prompt nativo, limpamos o evento que "morreu" 
          // e abrimos as instruções manuais como alternativa.
          setPromptEvent(null);
          setIsModalOpen(true);
        }
      } catch (error) {
        console.error('Falha no prompt de instalação:', error);
        // Fallback seguro em caso de falha da API do navegador
        setPromptEvent(null);
        setIsModalOpen(true);
      }
      return;
    }

    // Fallback geral se não houver promptEvent disponível desde o início
    setIsModalOpen(true);
  };

  // Hide the CTA button ONLY if already running in standalone mode
  if (isStandalone) {
    return null;
  }

  return (
    <>
      {/* Inline CTA Button */}
      <div className="w-full mt-3">
        <button
          onClick={handleInstallClick}
          className="w-full bg-white border border-zinc-200/90 hover:border-emerald-500/40 hover:bg-emerald-50/10 rounded-2xl p-3.5 shadow-2xs transition-all duration-300 flex items-center justify-between gap-3 cursor-pointer text-left group active:scale-[0.99]"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="bg-emerald-50 text-emerald-700 border border-emerald-200/80 p-2.5 rounded-xl shrink-0 group-hover:scale-105 transition-transform duration-300">
              <Smartphone className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xs font-bold text-zinc-900 leading-tight">Adicionar à Tela Inicial</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5 leading-normal truncate">
                Instale o R-MAX no celular e acesse diretamente sem navegador.
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* Manual Installation Instructions Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-100 flex items-end sm:items-center justify-center p-4 pointer-events-none">
            {/* Blurred Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-zinc-950/40 backdrop-blur-xs pointer-events-auto"
            />

            {/* Modal Container (Light theme) */}
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 350 }}
              className="relative w-full max-w-sm bg-white border border-zinc-100 rounded-3xl p-6 shadow-2xl overflow-hidden pointer-events-auto z-10 text-zinc-850"
            >
              {/* Background Glow */}
              <div className="absolute -top-12 -right-12 w-32 h-32 bg-brand/10 blur-2xl rounded-full pointer-events-none" />

              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 p-2 bg-zinc-100 hover:bg-zinc-200 rounded-full transition-colors text-zinc-500 cursor-pointer"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="flex flex-col items-center text-center gap-4 mt-2">
                <div className="bg-brand/10 p-4 rounded-2xl border border-brand/20">
                  <Smartphone className="h-8 w-8 text-brand" />
                </div>
                
                <div>
                  <h3 className="font-black text-zinc-900 text-base mb-1.5 leading-tight">
                    {isIOS ? 'Adicionar ao iPhone' : 'Como instalar o R-Max'}
                  </h3>
                  <p className="text-xs text-zinc-500 leading-relaxed max-w-xs mx-auto">
                    {isIOS 
                      ? 'Adicione à tela de início para usar o R-Max em tela cheia como um aplicativo no celular.'
                      : 'Adicione o atalho à sua tela inicial para abrir o aplicativo diretamente pelo celular.'}
                  </p>
                </div>
                
                {isIOS ? (
                  <div className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl p-4 text-left space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-1.5 rounded-lg border border-zinc-100 shrink-0">
                        <Share className="h-4 w-4 text-zinc-650" />
                      </div>
                      <p className="text-xs text-zinc-700 leading-relaxed">
                        1. Toque no botão de <strong>Compartilhar</strong> na barra inferior do Safari.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-1.5 rounded-lg border border-zinc-100 shrink-0">
                        <Plus className="h-4 w-4 text-zinc-650" />
                      </div>
                      <p className="text-xs text-zinc-700 leading-relaxed">
                        2. Role a lista de opções e toque em <strong>"Adicionar à Tela de Início"</strong>.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="w-full bg-zinc-50 border border-zinc-100 rounded-2xl p-4 text-left space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-1.5 rounded-lg border border-zinc-100 shrink-0">
                        <MoreVertical className="h-4 w-4 text-zinc-650" />
                      </div>
                      <p className="text-xs text-zinc-700 leading-relaxed">
                        1. Toque no menu do navegador (ícone de <strong>três pontos</strong> no canto superior direito).
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-white p-1.5 rounded-lg border border-zinc-100 shrink-0">
                        <Smartphone className="h-4 w-4 text-zinc-650" />
                      </div>
                      <p className="text-xs text-zinc-700 leading-relaxed">
                        2. Selecione a opção <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à tela inicial"</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
